import _ from "lodash";
import moment from "moment";
import momentTZ from "moment-timezone";

import {
  buildAddUpdateRequest,
  getAllottedTime,
  getDefaultEventTypeByUserType,
  getDefaultScheduleTypeOptions,
  getSelectableTimes,
  getSelectableTimesAvailable,
  getSelectableTimesAway,
  isChild,
  showDeleteAppointmentToast,
  showRescheduleAppointmentToast,
  validateAvailableAppointmentSelections,
  buildCalendarFilterFromPreferences,
} from "@/features/calendar/utils/general";
import { convertWorkingHourToLocal } from "@/features/calendar/utils/workingHours";
import { createDatesToDisplay } from "@/features/calendar/utils/dates";
import { isUserAdmin } from "@/utils/authToken";
import { providerRequests } from "@/api/(requests)/provider";
import { recurringScheduleWontCreateCalls } from "@/features/calendar/utils/event";
import { scheduleRequests } from "@/api/(requests)/schedule";
import { sharedRequests } from "@/api/(requests)/shared";

import { AVAILABLE, AWAY, isSpecialEvent } from "../../constants/event";
import { initialState } from "../reducers/calendarReducer";

import * as actionTypes from "./actionTypes";

export const initializeCalendar = (currentUserProfile) => (dispatch) => {
  const currentUserPreferences = currentUserProfile?.preferences;
  const currentUserCalendarPreferences = buildCalendarFilterFromPreferences(currentUserPreferences);

  dispatch({
    data: {
      selectedScheduleDate: moment().local(),
      ...currentUserCalendarPreferences,
    },
    type: actionTypes.CALENDAR_INITIALIZE,
  });
};

export const initializeProviderAsSelf = (currentUserProfile) => async (dispatch) => {
  const currentUserPreferences = currentUserProfile?.preferences;
  const timeZone = currentUserPreferences?.time_zone ?? "US/Eastern";

  dispatch({
    data: {
      provider: {
        localAvailability: convertWorkingHourToLocal(
          currentUserProfile?.availability,
          timeZone,
          momentTZ.tz.guess(),
        ),
        providerId: currentUserProfile?.username,
        providerType: currentUserProfile?.provider_type,
        timeZone,
      },
    },
    type: actionTypes.CALENDAR_INITIALIZE_PROVIDER,
  });
};

export const initializeProviderAsAdmin = (providerId) => async (dispatch) => {
  const provider = {
    localAvailability: null,
    providerId: providerId,
    providerType: null,
    timeZone: "US/Eastern",
  };

  dispatch({
    data: {
      provider,
    },
    type: actionTypes.CALENDAR_INITIALIZE_PROVIDER,
  });

  const searchParams = { providerId };
  const userProfile = (await providerRequests.fetch_provider_profile({ searchParams }))?.user;

  provider.providerType = userProfile?.provider_type;
  provider.timeZone = userProfile?.preferences?.time_zone ?? "US/Eastern";
  provider.localAvailability = convertWorkingHourToLocal(
    userProfile?.availability,
    provider.timeZone,
    momentTZ.tz.guess(),
  );
  provider.name = userProfile?.name;

  dispatch({
    data: { provider },
    type: actionTypes.CALENDAR_INITIALIZE_PROVIDER,
  });
};

export const initializeOnBehalfOfProviderPatient = (providerId, patientId) => async (dispatch) => {
  const provider = {
    localAvailability: null,
    providerId: providerId,
    providerType: null,
    timeZone: "US/Eastern",
  };

  dispatch({
    data: {
      patientId: patientId,
      provider: provider,
    },
    type: actionTypes.CALENDAR_INITIALIZE_BEHALF_OF_PROVIDER_PATIENT,
  });

  const searchParams = { patientId, providerId };
  const userProfile = (await providerRequests.fetch_provider_profile_on_behalf_of({ searchParams }))
    ?.user;

  provider.providerType = userProfile?.provider_type;
  provider.timeZone = userProfile?.preferences?.time_zone ?? "US/Eastern";
  provider.localAvailability = convertWorkingHourToLocal(
    userProfile?.availability,
    provider.timeZone,
    momentTZ.tz.guess(),
  );
  provider.name = userProfile?.name;

  const patientData = await dispatch(fetchPatient(patientId));

  dispatch(updateAppointmentPatient({ patient_id: patientId }));

  dispatch({
    data: {
      acuityScore: patientData?.user?.acuity_score,
      patientId,
      provider,
    },
    type: actionTypes.CALENDAR_INITIALIZE_BEHALF_OF_PROVIDER_PATIENT,
  });
};

const isOnBehalfOfProviderPatientScheduling = () => (_dispatch, getState) => {
  const calendarState = getState().calendar;

  return Boolean(calendarState.onBehalfOfPatient);
};

export const fetchEventTypeOptions = (provider, patient) => async (dispatch) => {
  dispatch({
    data: {
      acuityScore: patient?.acuity_score,
      eventTypeOptions: await getDefaultScheduleTypeOptions(provider, patient),
    },
    type: actionTypes.CALENDAR_FETCH_EVENT_TYPE_OPTIONS,
  });
};

export const fetchPatients =
  ({ searchParams } = {}) =>
  (dispatch) => {
    return providerRequests.fetch_patient_list({ searchParams }).then((data) => {
      const activePatients = data.user.filter((patient) => patient.patient_status !== "INACTIVE");

      const patientDropdownOptions = activePatients.map((patient) => {
        const { dob, first_name, last_name, preferred_name } = patient;

        return {
          label: `${first_name} ${last_name}${preferred_name ? ` (${preferred_name})` : ""}, ${dob}`,
          value: patient.patient_id,
        };
      });

      const patientMap = activePatients.reduce((map, patient) => {
        map[patient["patient_id"]] = patient;

        return map;
      }, {});

      dispatch(updatePatientOptions({ patientDropdownOptions, patientMap }));
    });
  };

export const fetchPatient = (patientId) => async (dispatch) => {
  const urlParams = { patientId };
  const data = await sharedRequests.fetch_patient_details({ urlParams });

  const patient = data.user;

  patient["patient_id"] = patient.username;

  dispatch(
    updatePatientOptions({
      patientDropdownOptions: [
        {
          label: `${patient.first_name} ${patient.last_name}${
            patient.preferred_name ? ` (${patient.preferred_name})` : ""
          }, ${patient.dob}`,
          value: patientId,
        },
      ],
      patientMap: {
        [patientId]: patient,
      },
    }),
  );

  return data;
};

const updatePatientOptions = (data) => {
  return {
    data,
    type: actionTypes.CALENDAR_FETCH_PATIENT_OPTIONS,
  };
};

/**
 * updates the selected date and fetches the schedule for the current day
 * @param {*} selectedDate
 * @returns
 */
export const updateSelectedDate = (selectedDate) => (dispatch, getState) => {
  const calendarState = getState().calendar;
  const { provider, viewType } = calendarState;
  const currentDatesToDisplay = calendarState?.datesToDisplay ?? [];

  if (isUserAdmin() && !provider?.providerId) {
    return;
  }

  const datesToDisplay = createDatesToDisplay(selectedDate, viewType);

  // Set new selected dates
  dispatch(
    updateSelectedScheduleDates({
      selectedScheduleDate: selectedDate,
      selectedScheduleDates: datesToDisplay,
    }),
  );

  // only refetch the calendar if it is a new date
  if (currentDatesToDisplay.some((date) => date.isSame(selectedDate))) {
    return;
  }
  dispatch(updateScheduledEvents());

  if (calendarState.unconfirmedEvent) {
    dispatch(upsertUnconfirmedEvent(selectedDate));
  }
};

/**
 * updates the selected date and fetches the schedule for the current day
 * @param {*} selectedDate
 * @returns
 */
export const updateSelectedDateV2 =
  (selectedDate, selectedEventId = null) =>
  (dispatch, getState) => {
    const calendarState = getState().calendar;
    const { provider, viewType } = calendarState;
    const currentDatesToDisplay = calendarState?.datesToDisplay ?? [];

    if (isUserAdmin() && !provider?.providerId) {
      return;
    }

    const datesToDisplay = createDatesToDisplay(selectedDate, viewType).map((date) =>
      moment(date, "YYYY-MM-DD"),
    );

    // Set new selected dates
    dispatch(
      updateSelectedScheduleDates({
        selectedScheduleDate: selectedDate,
        selectedScheduleDates: datesToDisplay,
      }),
    );

    // only refetch the calendar if it is a new date
    if (currentDatesToDisplay.some((date) => date.isSame(selectedDate))) {
      return;
    }

    dispatch(updateScheduledEventsV2(selectedEventId));

    if (calendarState.unconfirmedEvent) {
      dispatch(upsertUnconfirmedEvent(selectedDate));
    }
  };

const updateScheduledEvents = () => (dispatch, getState) => {
  const calendarState = getState().calendar;
  const { provider, selectedScheduleDates } = calendarState;
  const startDate = _.cloneDeep(selectedScheduleDates[0]).startOf("day");
  const endDate = _.cloneDeep(selectedScheduleDates.slice(-1)[0]).endOf("day");

  const isOnBehalfProviderPatient = Boolean(calendarState.onBehalfOfPatient);

  const searchParams = {
    local_end_date_time: endDate.format("YYYY-MM-DD HH:mm"),
    local_start_date_time: startDate.format("YYYY-MM-DD HH:mm"),
    provider_id: provider?.providerId,
    show_deleted: true,
    show_unjoinable: true,
    timezone: moment.tz.guess(),
  };

  if (isOnBehalfProviderPatient) {
    searchParams["patient_id"] = calendarState.onBehalfOfPatient;
  }

  return scheduleRequests
    .get_schedule({
      context: { showLoader: false },
      searchParams,
    })
    .then((data) => {
      dispatch(
        updateProviderScheduleCallList({
          events: [...data.schedule, ...data.away, ...data.availability],
        }),
      );
      dispatch(updateTimeSlots());
    });
};

const updateScheduledEventsV2 =
  (selectedEventId = null) =>
  (dispatch, getState) => {
    const calendarState = getState().calendar;
    const { provider, selectedScheduleDates } = calendarState;

    const startDate = _.cloneDeep(selectedScheduleDates[0]).startOf("day");
    const endDate = _.cloneDeep(selectedScheduleDates.slice(-1)[0]).endOf("day");

    return scheduleRequests
      .get_schedule({
        context: { showLoader: false },
        searchParams: {
          local_end_date_time: endDate.format("YYYY-MM-DD HH:mm"),
          local_start_date_time: startDate.format("YYYY-MM-DD HH:mm"),
          provider_id: provider?.providerId,
          show_deleted: true,
          show_unjoinable: true,
          timezone: moment.tz.guess(),
        },
      })
      .then((data) => {
        dispatch(
          updateProviderScheduleCallList({
            events: [...data.schedule, ...data.away, ...data.availability],
          }),
        );
        dispatch(updateTimeSlots());

        // Find the specific event by ID if selectedEventId is provided
        if (selectedEventId) {
          const selectedEvent = data.schedule.find((event) => event.callId === selectedEventId);

          if (selectedEvent) {
            dispatch(updateSelectedEvents(selectedEvent));
          }
        }
      });
  };

const updateSelectedScheduleDates = (data) => (dispatch) => {
  dispatch({
    data,
    type: actionTypes.CALENDAR_UPDATE_DATE,
  });

  dispatch(updateTimeSlots());
};

export const openAppointmentPicker = (data) => (dispatch) => {
  dispatch({
    data,
    type: actionTypes.OPEN_APPOINTMENT_PICKER,
  });
  dispatch(updateTimeSlots());
  dispatch(upsertUnconfirmedEvent(data.selectedScheduleDate));
};

export const closeDrawer = () => (dispatch, getState) => {
  const calendarState = getState().calendar;

  dispatch(deleteUnconfirmedEvent());
  const data = getAppointmentSelectionsAfterReset(calendarState);

  dispatch({
    data,
    type: actionTypes.CLOSE_DRAWER,
  });
};

const upsertUnconfirmedEvent =
  (overrideDate = null) =>
  (dispatch, getState) => {
    const calendarState = getState().calendar;
    const { appointmentSelections, patientMap, selectedScheduleDate } = calendarState;
    const { endTime, eventType, patientId, startTime, time } = appointmentSelections;
    const date = overrideDate ? overrideDate : selectedScheduleDate;

    if (isSpecialEvent(eventType)) {
      // if the start or end time is not valid default to 0 minutes
      let data = {
        allotted_time: 0,
        unconfirmed: true,
      };

      if (date && startTime && endTime) {
        const localTimestamp = `${date.format("YYYY-MM-DD")} ${startTime}`;
        const timestamp = moment(localTimestamp).utc().format("YYYY-MM-DD HH:mm");
        const allottedTime = Math.max(
          moment(endTime, "HH:mm").diff(moment(startTime, "HH:mm"), "minutes"),
          0,
        );

        data = {
          allotted_time: allottedTime,
          timestamp,
          unconfirmed: true,
        };
      }
      dispatch({
        data,
        type: actionTypes.CALENDAR_UPSERT_UNCONFIRMED_EVENT,
      });
    } else if (date && time) {
      const localTimestamp = `${date.format("YYYY-MM-DD")} ${time}`;
      const timestamp = moment(localTimestamp).utc().format("YYYY-MM-DD HH:mm");
      const isPatientChild = patientId ? isChild(patientMap[patientId]) : false;
      const allottedTime = getAllottedTime(eventType, isPatientChild);

      dispatch({
        data: {
          allotted_time: allottedTime,
          timestamp,
          unconfirmed: true,
        },
        type: actionTypes.CALENDAR_UPSERT_UNCONFIRMED_EVENT,
      });
    }
  };

const deleteUnconfirmedEvent = () => (dispatch) => {
  dispatch({
    type: actionTypes.CALENDAR_DELETE_UNCONFIRMED_EVENT,
  });
};

/**
 * updates the selectable time slots on the appointment picker
 * @returns
 */
const updateTimeSlots = () => (dispatch, getState) => {
  const calendar = getState().calendar;
  const { events, selectedScheduleDate } = calendar;
  const workingHours = calendar.provider.localAvailability;
  const { awayDuration, endTime, eventType, patientId, startTime, time } =
    calendar.appointmentSelections;
  const patient = calendar.patientMap[patientId];
  const filteredEvents = events.filter(
    (event) => event.callId !== calendar.appointmentSelections.callId && event.deleted !== true,
  );
  const date = moment.utc(selectedScheduleDate, "YYYY-MM-DD HH:mm").local();

  // if the event type is available then all working hours should be filtered out
  if (eventType === AVAILABLE) {
    const { fromSlots, toSlots } = getSelectableTimesAvailable(
      date,
      workingHours,
      filteredEvents.filter((event) => event.event_type === AVAILABLE),
      filteredEvents.filter((event) => event.event_type === AWAY),
    );

    const shouldRemoveTimeFrom =
      startTime &&
      !fromSlots.some((timeSlot) => {
        return timeSlot.value === startTime;
      });

    const shouldRemoveTimeTo =
      endTime &&
      !toSlots.some((timeSlot) => {
        return timeSlot.value === endTime;
      });

    if (shouldRemoveTimeFrom) {
      dispatch(updateAppointmentStartTime(null));
    }

    if (shouldRemoveTimeTo) {
      dispatch(updateAppointmentEndTime(null));
    }

    dispatch(updateSelectableTimesFrom(fromSlots));
    dispatch(updateSelectableTimesTo(toSlots));
  } else if (eventType === AWAY) {
    const { fromSlots, toSlots } = getSelectableTimesAway(filteredEvents, date);

    const shouldRemoveTimeFrom =
      startTime &&
      !fromSlots.some((timeSlot) => {
        return timeSlot.value === startTime;
      });

    const shouldRemoveTimeTo =
      endTime &&
      !toSlots.some((timeSlot) => {
        return timeSlot.value === endTime;
      });

    if (shouldRemoveTimeFrom) {
      dispatch(updateAppointmentStartTime(null));
    }

    if (shouldRemoveTimeTo) {
      dispatch(updateAppointmentEndTime(null));
    }

    dispatch(updateSelectableTimesFrom(fromSlots));
    dispatch(updateSelectableTimesTo(toSlots));
  } else {
    const allottedTime = eventType === AWAY ? parseInt(awayDuration) : null;
    const selectableTimeSlots = getSelectableTimes({
      allottedTime,
      date,
      events: filteredEvents,
      eventType,
      patient,
    });

    const shouldRemoveTime =
      time &&
      !selectableTimeSlots.some((timeSlot) => {
        return timeSlot.value === time;
      });

    if (shouldRemoveTime) {
      dispatch(clearTimes(null));
    }

    dispatch(updateSelectableTimes(selectableTimeSlots));
  }
};

const updateProviderScheduleCallList = (data) => {
  return {
    data,
    type: actionTypes.CALENDAR_UPDATE_SCHEDULE_CALL_LIST,
  };
};

export const updateAppointmentEventType = (eventType) => (dispatch) => {
  dispatch({
    data: { eventType },
    type: actionTypes.CALENDAR_UPDATE_APPOINTMENT_SELECTIONS,
  });
  dispatch(updateTimeSlots());
  dispatch(upsertUnconfirmedEvent());
};

/**
 * redux action to update the times selectable from the appointment picker
 * @param {*} selectableTimes
 * @returns
 */
const updateSelectableTimes = (selectableTimes) => (dispatch) => {
  dispatch({
    data: { selectableTimes },
    type: actionTypes.CALENDAR_UPDATE_APPOINTMENT_SELECTIONS,
  });
};

const updateSelectableTimesFrom = (selectableTimesFrom) => (dispatch) => {
  dispatch({
    data: { selectableTimesFrom },
    type: actionTypes.CALENDAR_UPDATE_APPOINTMENT_SELECTIONS,
  });
};

const updateSelectableTimesTo = (selectableTimesTo) => (dispatch) => {
  dispatch({
    data: { selectableTimesTo },
    type: actionTypes.CALENDAR_UPDATE_APPOINTMENT_SELECTIONS,
  });
};

export const updateAppointmentTime = (time) => (dispatch) => {
  dispatch({
    data: { time },
    type: actionTypes.CALENDAR_UPDATE_APPOINTMENT_SELECTIONS,
  });
  dispatch(upsertUnconfirmedEvent());
};

const clearTimes = () => (dispatch) => {
  dispatch({
    data: {
      time: null,
    },
    type: actionTypes.CALENDAR_UPDATE_APPOINTMENT_SELECTIONS,
  });
};

export const updateAppointmentStartTime = (startTime) => (dispatch) => {
  dispatch({
    data: { startTime },
    type: actionTypes.CALENDAR_UPDATE_APPOINTMENT_SELECTIONS,
  });

  dispatch(upsertUnconfirmedEvent());
};

export const updateAppointmentEndTime = (endTime) => (dispatch) => {
  dispatch({
    data: { endTime },
    type: actionTypes.CALENDAR_UPDATE_APPOINTMENT_SELECTIONS,
  });

  dispatch(upsertUnconfirmedEvent());
};

export const updateAppointmentPatient = (patient) => (dispatch) => {
  dispatch({
    data: { patientId: patient.patient_id },
    type: actionTypes.CALENDAR_UPDATE_APPOINTMENT_SELECTIONS,
  });
};

export const updateAppointmentLabel = (label) => (dispatch) => {
  dispatch({
    data: { label },
    type: actionTypes.CALENDAR_UPDATE_APPOINTMENT_SELECTIONS,
  });
};

export const updateAppointmentIsReoccurring = (isReoccurring) => (dispatch, getState) => {
  dispatch({
    data: {
      seriesData: {
        frequencyDays: [moment(getState().calendar?.selectedScheduleDate, "YYYY-MM-DD").day()],
        frequencyWeeks: 0,
        isReoccurring: isReoccurring,
        numberOfOccurrences: 1,
      },
    },
    type: actionTypes.CALENDAR_UPDATE_APPOINTMENT_SELECTIONS,
  });
};

export const updateAppointmentFrequencyDays = (frequencyDays) => (dispatch) => {
  dispatch({
    data: { seriesData: { frequencyDays } },
    type: actionTypes.CALENDAR_UPDATE_APPOINTMENT_SELECTIONS,
  });
};

export const updateAppointmentFrequencyWeeks = (frequencyWeeks) => (dispatch) => {
  dispatch({
    data: { seriesData: { frequencyWeeks } },
    type: actionTypes.CALENDAR_UPDATE_APPOINTMENT_SELECTIONS,
  });
};

export const updateAppointmentNumberOfOccurrences = (numberOfOccurrences) => (dispatch) => {
  dispatch({
    data: { seriesData: { numberOfOccurrences } },
    type: actionTypes.CALENDAR_UPDATE_APPOINTMENT_SELECTIONS,
  });
};

/**
 * added as apart of https://aptihealth.atlassian.net/browse/AT1-6273
 * in order to lock the calendar to only viewing the patient x provider
 * combo
 * @param {*} state
 * @returns undefined if the state should be reset to the initial state,
 * otherwise returns a new state where some values have been retained
 */
const getAppointmentSelectionsAfterReset = (calendarState) => {
  const isOnBehalfProviderPatient = Boolean(calendarState.onBehalfOfPatient);
  let newAppointmentSelectionData = undefined;

  // if on behalf of scheduling do not reset the patient relevant appointment selections
  if (isOnBehalfProviderPatient) {
    newAppointmentSelectionData = {};
    const newAppointmentSelections = _.cloneDeep(initialState.appointmentSelections);
    const keysToRetain = ["patientId", "acuityScore"];

    for (const key of keysToRetain) {
      newAppointmentSelections[key] = calendarState.appointmentSelections[key];
    }

    const defaultEventType = getDefaultEventTypeByUserType(calendarState?.provider?.providerType);

    newAppointmentSelections["eventType"] = defaultEventType;

    newAppointmentSelectionData.appointmentSelections = newAppointmentSelections;
  }

  return newAppointmentSelectionData;
};

export const resetAppointmentSelections = () => (dispatch, getState) => {
  const calendarState = getState().calendar;
  const data = getAppointmentSelectionsAfterReset(calendarState);

  dispatch({
    data,
    type: actionTypes.CALENDAR_RESET_APPOINTMENT_SELECTIONS,
  });
};

export const resetSchedulingMode = () => (dispatch) => {
  dispatch({
    type: actionTypes.CALENDAR_RESET_SCHEDULING_MODE,
  });
};

export const refetchSchedule = () => (dispatch) => {
  dispatch(updateScheduledEvents());
};

export const deleteAppointment =
  (callId, cancellation_reason, twenty_four_hour_notice, delete_series) => (dispatch) => {
    const data = { delete_series };

    // Only include cancellation_reason if provided (VIDEO sessions require it, AWAY/AVAILABLE do not)
    if (cancellation_reason !== null && cancellation_reason !== undefined) {
      data.cancellation_reason = {
        reason_type: cancellation_reason,
        twenty_four_hour_notice: twenty_four_hour_notice === "yes",
      };
    }

    return scheduleRequests
      .delete_schedule({
        data,
        searchParams: { call_id: callId },
      })
      .then((data) => {
        dispatch(refetchSchedule());
        dispatch(resetAppointmentSelections());
        dispatch(closeDrawer());
        showDeleteAppointmentToast(data, dispatch);
      })
      .catch((error) => {
        console.error(error);
        dispatch(refetchSchedule());
      });
  };

export const addAppointment = () => (dispatch, getState) => {
  const calendarState = getState().calendar;
  const appointment = calendarState.appointmentSelections;

  const errors = validateAppointmentSelections(appointment);

  dispatch({
    data: { appointmentSelectionErrors: errors },
    type: actionTypes.CALENDAR_UPDATE_APPOINTMENT_SELECTION_ERRORS,
  });

  const hasErrors = Object.keys(errors).some((key) => errors[key]);

  if (hasErrors) {
    return;
  }

  const patientId = appointment.patientId;
  const patient = calendarState.patientMap[patientId];
  const seriesData = appointment.seriesData;

  const data = buildAddUpdateRequest(
    seriesData,
    appointment.time,
    calendarState.selectedScheduleDate.format("YYYY-MM-DD"),
    appointment.eventType,
    patient,
    calendarState?.provider?.providerId,
    appointment.startTime,
    appointment.endTime,
    appointment.label,
  );

  return scheduleRequests.post_schedule({ data }).then((data) => {
    // Creating a series only returns the first call created, so we re-fetch all
    if (seriesData.isReoccurring) {
      dispatch(updateScheduledEvents());
    } else {
      dispatch(
        updateProviderScheduleCallList({
          events: [...calendarState.events, data.appointment_details],
        }),
      );
    }

    dispatch(closeDrawer());
    dispatch(resetAppointmentSelections());
    dispatch(fetchEventTypeOptions(calendarState.provider));
    dispatch(
      updateAppointmentEventType(
        getDefaultEventTypeByUserType(calendarState.provider?.providerType),
      ),
    );
  });
};

const validateAppointmentSelections = (appointmentSelections) => {
  let appointmentSelectionErrors = {
    endTime: null,
    eventType: null,
    patientId: null,
    seriesData: null,
    startTime: null,
    time: null,
  };

  if (!appointmentSelections.eventType) {
    appointmentSelectionErrors.eventType = "Appointment type is required";
  }

  if (isSpecialEvent(appointmentSelections.eventType)) {
    appointmentSelectionErrors = validateAvailableAppointmentSelections(
      appointmentSelections,
      appointmentSelectionErrors,
    );
  } else {
    if (
      appointmentSelections.eventType !== AWAY &&
      appointmentSelections.eventType !== AVAILABLE &&
      !appointmentSelections.patientId
    ) {
      appointmentSelectionErrors.patientId = "Patient is required";
    }

    if (!appointmentSelections.time) {
      appointmentSelectionErrors.time = "Time is required";
    }
  }

  const seriesData = appointmentSelections.seriesData;

  if (
    seriesData?.isReoccurring &&
    (seriesData?.frequencyDays.length === 0 ||
      seriesData?.frequencyWeeks === null ||
      seriesData.numberOfOccurrences === null)
  ) {
    appointmentSelectionErrors.seriesData = "Reoccurrence details required";
  } else if (recurringScheduleWontCreateCalls(seriesData)) {
    appointmentSelectionErrors.seriesData = "This recurring schedule would create no appointments";
  }

  return appointmentSelectionErrors;
};

export const updateCalendarView = (viewType) => (dispatch) => {
  dispatch({
    data: { viewType },
    type: actionTypes.CALENDAR_UPDATE_VIEW_TYPE,
  });
};

export const updateSelectedEvents = (selectedEvents) => (dispatch, getState) => {
  const calendarState = getState().calendar;

  selectedEvents = Array.isArray(selectedEvents) ? selectedEvents : [selectedEvents];
  const data = getAppointmentSelectionsAfterReset(calendarState);

  dispatch({
    data: { selectedEvents, ...data },
    type: actionTypes.CALENDAR_UPDATE_SELECTED_EVENTS,
  });
};

export const editAppointmentSelected = (event) => (dispatch) => {
  dispatch({
    data: { event },
    type: actionTypes.CALENDAR_EDIT_APPOINTMENT_SELECTED,
  });
  dispatch(updateTimeSlots());
};

export const checkEditUntouched = () => (dispatch, getState) => {
  const calendarState = getState().calendar;
  const existingCall = calendarState.events.find(
    (event) => event.callId === calendarState.appointmentSelections.callId,
  );

  if (!existingCall) {
    dispatch({
      type: actionTypes.CALENDAR_RESET_APPOINTMENT_EDIT_SELECTION_ERRORS,
    });

    return;
  }

  const appointmentEditSelectionErrors = {
    untouched:
      moment.utc(existingCall.timestamp).tz(momentTZ.tz.guess()).format("HH:mm") ===
        calendarState?.appointmentSelections?.time &&
      moment.utc(existingCall?.timestamp).format("YYYY-MM-DD") ===
        moment(calendarState?.selectedScheduleDate).format("YYYY-MM-DD") &&
      existingCall?.patient_username === calendarState?.appointmentSelections?.patientId &&
      existingCall?.event_type === calendarState?.appointmentSelections?.eventType,
  };

  dispatch({
    data: { appointmentEditSelectionErrors },
    type: actionTypes.CALENDAR_UPDATE_APPOINTMENT_EDIT_SELECTION_ERRORS,
  });
};

export const editAppointment = () => (dispatch, getState) => {
  const calendarState = getState().calendar;
  const appointment = calendarState.appointmentSelections;
  const patientId = appointment.patientId;
  const patient = calendarState.patientMap[patientId];
  const seriesData = appointment.seriesData;
  const callId = appointment.callId;

  const errors = validateAppointmentSelections(appointment);

  dispatch({
    data: { appointmentSelectionErrors: errors },
    type: actionTypes.CALENDAR_UPDATE_APPOINTMENT_SELECTION_ERRORS,
  });

  const hasErrors = Object.keys(errors).some((key) => errors[key]);

  if (hasErrors) {
    return;
  }

  const requestBody = buildAddUpdateRequest(
    seriesData,
    appointment.time,
    calendarState.selectedScheduleDate.format("YYYY-MM-DD"),
    appointment.eventType,
    patient,
    calendarState?.provider?.providerId,
    appointment.startTime,
    appointment.endTime,
    appointment.label,
  );

  const data = { ...requestBody, call_id: callId };

  scheduleRequests
    .put_schedule({ data })
    .then((data) => {
      showRescheduleAppointmentToast(data, dispatch);
      dispatch(refetchSchedule());
    })
    .catch((error) => {
      console.error(error);
      dispatch(refetchSchedule());
    });
  dispatch(resetAppointmentSelections());
  dispatch(closeDrawer());
};

export const updateCalendarFilters = (filters) => (dispatch) => {
  dispatch({
    data: { filters },
    type: actionTypes.CALENDAR_UPDATE_FILTERS,
  });
};

const resetCalendarFilters = () => (dispatch) => {
  dispatch({
    type: actionTypes.CALENDAR_RESET_FILTERS,
  });
};

export const toggleDatePicker = () => (dispatch) => {
  dispatch({
    type: actionTypes.CALENDAR_TOGGLE_DATEPICKER,
  });
};

export const updateTimezone = (timezone) => (dispatch) => {
  dispatch({
    data: { timezone },
    type: actionTypes.CALENDAR_UPDATE_TIMEZONE,
  });
};
